import React from "react"
import {useTranslation} from "react-i18next"
import {
  Bar,
  BarChart,
  DefaultZIndexes,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  usePlotArea,
  useYAxisScale,
  XAxis,
  XAxisTickContentProps,
  YAxis,
  ZIndexLayer,
} from "recharts"

import {useKlaraUpdatesOverTimeQuery} from "../../../queries/klaraDashboard.ts"
import {AKlaraDashboardBucket} from "../../../services/types.generated.ts"
import {averagePerBucket, bucketLabel, bucketTotal, granularityFor, TKlaraGranularity} from "../buckets.ts"
import {getPeriodRange} from "../period.ts"
import {dashboardDay} from "../useDashboardClock.ts"
import {useKlaraPeriod} from "../useKlaraPeriod.ts"
import {CardState} from "./CardState.tsx"
import {CARD_TITLE_CLASS_NAME} from "./cardStyles.ts"

const VOICE_COLOR = "var(--color-cr-blue)"
const MANUAL_COLOR = "var(--color-cr-blue-mid-light)"
const AXIS_STROKE = "var(--color-cr-grey-15)"
const TOP_LINE_STROKE = "var(--color-cr-slate-15)"
const AVERAGE_STROKE = "var(--color-cr-blue-dark)"
const CURSOR_FILL = "var(--color-cr-slate-2)"
const MUTED_TEXT = "var(--color-cr-slate-40)"
const LABEL_FONT_SIZE = 10
const EDGE_LABEL_FONT_SIZE = 10.5

// A ~110px plot above the 20px strip of edge labels.
const CHART_HEIGHT = 134
const X_AXIS_HEIGHT = 20
// Room on the left for the maximum's label; the top keeps that label inside the frame.
const CHART_MARGIN = {top: 4, right: 0, left: 24, bottom: 0}
// The average's label sits above its line unless that would leave the plot, then below it.
const AVERAGE_LABEL_ABOVE = 5
const AVERAGE_LABEL_BELOW = 12
const BAR_GAP = 2

type TChartRow = {label: string; voice: number; manual: number; total: number}

const toRows = (buckets: readonly AKlaraDashboardBucket[], granularity: TKlaraGranularity): TChartRow[] =>
  buckets.map(bucket => ({
    label: bucketLabel(bucket, granularity),
    voice: bucket.voice,
    manual: bucket.manual,
    total: bucketTotal(bucket),
  }))

const formatCount = (value: number) => value.toLocaleString(undefined, {maximumFractionDigits: 1})

/** The team's Klara updates over the period, stacked by source (voice / manual), one bar per bucket. */
export const UpdatesChartCard: React.FC = () => {
  const {t} = useTranslation()
  const [period] = useKlaraPeriod()
  const titleId = React.useId()
  // The picker computes preset dates on the browser's calendar.
  const range = React.useMemo(() => getPeriodRange(period), [period])
  const requestedGranularity = granularityFor(range)
  const {data, isPending, isError, isFetching, refetch} = useKlaraUpdatesOverTimeQuery({
    ...range,
    granularity: requestedGranularity,
  })
  const response = data?.data
  const granularity = response?.granularity ?? requestedGranularity
  const title = {
    day: t("KlaraDashboard_UpdatesChart_Title_Day"),
    week: t("KlaraDashboard_UpdatesChart_Title_Week"),
    month: t("KlaraDashboard_UpdatesChart_Title_Month"),
  }[granularity]

  return (
    <section aria-labelledby={titleId} className={"flex min-w-0 flex-[1.6_1_320px] flex-col gap-3.5"}>
      <h2 id={titleId} className={CARD_TITLE_CLASS_NAME}>
        {title}
      </h2>
      <CardState query={{isPending, isError, isFetching, refetch}} skeletonClassName={"h-40"}>
        {response &&
          (response.buckets.length === 0 ? (
            <p
              data-testid={"updates chart empty"}
              className={"flex h-40 items-center justify-center text-center text-sm text-cr-slate-60"}
            >
              {t("KlaraDashboard_UpdatesChart_Empty")}
            </p>
          ) : (
            <UpdatesChart
              title={title}
              buckets={response.buckets}
              granularity={response.granularity}
              today={dashboardDay({asOf: new Date(response.as_of), timeZone: response.time_zone})}
            />
          ))}
      </CardState>
    </section>
  )
}

type TUpdatesChartProps = {
  title: string
  buckets: readonly AKlaraDashboardBucket[]
  granularity: TKlaraGranularity
  /** The dashboard's calendar date (YYYY-MM-DD); a range ending on it reads "Today" at its end. */
  today: string
}

const UpdatesChart: React.FC<TUpdatesChartProps> = ({title, buckets, granularity, today}) => {
  const {t} = useTranslation()
  const rows = React.useMemo(() => toRows(buckets, granularity), [buckets, granularity])
  const average = averagePerBucket(buckets)
  const max = Math.max(0, ...rows.map(row => row.total))
  const endLabel = buckets[buckets.length - 1].end_date === today ? t("KlaraDashboard_UpdatesChart_Today") : undefined

  const renderTick = React.useCallback(
    (props: XAxisTickContentProps) => <EdgeTick {...props} count={rows.length} endLabel={endLabel} />,
    [rows.length, endLabel]
  )

  const renderTooltip = React.useCallback(({active, payload}: TooltipContentProps) => {
    if (!active || !payload?.length) {
      return null
    }
    return <UpdatesTooltip row={payload[0].payload as TChartRow} />
  }, [])

  return (
    <>
      <div className={"w-full"} style={{height: CHART_HEIGHT}} data-testid={"updates chart"}>
        <ResponsiveContainer width={"100%"} height={"100%"}>
          <BarChart data={rows} margin={CHART_MARGIN} barCategoryGap={BAR_GAP} aria-label={title}>
            <XAxis
              dataKey={"label"}
              interval={0}
              tickLine={false}
              tickSize={0}
              tickMargin={6}
              height={X_AXIS_HEIGHT}
              axisLine={{stroke: AXIS_STROKE}}
              tick={renderTick}
            />
            {/* The tallest bar reaches the top line, so the maximum's label reads as the scale. */}
            <YAxis hide domain={[0, Math.max(max, 1)]} allowDataOverflow />
            <Tooltip content={renderTooltip} cursor={{fill: CURSOR_FILL}} />
            <Bar dataKey={"voice"} stackId={"updates"} fill={VOICE_COLOR} />
            <Bar dataKey={"manual"} stackId={"updates"} fill={MANUAL_COLOR} radius={[2, 2, 0, 0]} />
            <ChartGuides max={max} average={average} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <BucketsTable title={title} rows={rows} />
    </>
  )
}

// Only the first and last buckets are labelled, at the plot's edges, so the range's start and end read at a glance.
const EdgeTick: React.FC<XAxisTickContentProps & {count: number; endLabel?: string}> = ({
  y,
  payload,
  className,
  count,
  endLabel,
}) => {
  const plot = usePlotArea()
  const index = payload.index
  if (!plot || (index !== 0 && index !== count - 1)) {
    return null
  }
  const isEnd = index === count - 1 && count > 1
  return (
    <text
      className={className}
      x={isEnd ? plot.x + plot.width : plot.x}
      y={y}
      dy={"0.71em"}
      textAnchor={isEnd ? "end" : "start"}
      fontSize={EDGE_LABEL_FONT_SIZE}
      fill={MUTED_TEXT}
    >
      {isEnd && endLabel ? endLabel : String(payload.value)}
    </text>
  )
}

// The dashed top line with the maximum on the left, and the dashed average with its value above its right end.
// Placed from the plot area and the value scale. (useXAxisTicks can't be used here: under StrictMode its state is
// left empty after the effects re-run.)
const ChartGuides: React.FC<{max: number; average: number}> = ({max, average}) => {
  const {t} = useTranslation()
  const plot = usePlotArea()
  const yScale = useYAxisScale()
  const averageY = yScale?.(average)
  if (!plot || averageY === undefined) {
    return null
  }
  const right = plot.x + plot.width
  const averageLabelY =
    averageY - AVERAGE_LABEL_ABOVE - LABEL_FONT_SIZE < plot.y
      ? averageY + AVERAGE_LABEL_BELOW
      : averageY - AVERAGE_LABEL_ABOVE

  return (
    <ZIndexLayer zIndex={DefaultZIndexes.label}>
      <g pointerEvents={"none"}>
        <line x1={plot.x} x2={right} y1={plot.y} y2={plot.y} stroke={TOP_LINE_STROKE} strokeDasharray={"4 3"} />
        {max > 0 && (
          <text
            className={"updates-max"}
            x={plot.x - 4}
            y={plot.y}
            dy={"0.32em"}
            textAnchor={"end"}
            fontSize={LABEL_FONT_SIZE}
            fill={MUTED_TEXT}
          >
            {formatCount(max)}
          </text>
        )}
        <line
          className={"updates-average"}
          x1={plot.x}
          x2={right}
          y1={averageY}
          y2={averageY}
          stroke={AVERAGE_STROKE}
          strokeWidth={1.5}
          strokeDasharray={"4 3"}
        />
        <text
          className={"updates-average-label"}
          x={right - 3}
          y={averageLabelY}
          textAnchor={"end"}
          fontSize={LABEL_FONT_SIZE}
          fontWeight={700}
          fill={AVERAGE_STROKE}
          stroke={"var(--color-cr-white)"}
          strokeWidth={3}
          paintOrder={"stroke"}
        >
          {t("KlaraDashboard_UpdatesChart_Average", {value: formatCount(average)})}
        </text>
      </g>
    </ZIndexLayer>
  )
}

const UpdatesTooltip: React.FC<{row: TChartRow}> = ({row}) => {
  const {t} = useTranslation()

  return (
    <div
      role={"status"}
      aria-live={"polite"}
      aria-atomic={"true"}
      className={
        "flex flex-col gap-0.5 rounded-[10px] bg-cr-blue-dark px-3 py-2 text-[11.5px] text-cr-white shadow-lg pixel-units"
      }
    >
      <span className={"font-bold"}>{row.label}</span>
      <span>{t("KlaraDashboard_UpdatesChart_Total", {count: row.total})}</span>
      <LegendItem color={VOICE_COLOR} label={t("KlaraDashboard_UpdatesChart_VoiceCount", {count: row.voice})} />
      <LegendItem color={MANUAL_COLOR} label={t("KlaraDashboard_UpdatesChart_ManualCount", {count: row.manual})} />
    </div>
  )
}

const LegendItem: React.FC<{color: string; label: string}> = ({color, label}) => (
  <span className={"flex items-center gap-2 whitespace-nowrap"}>
    <span aria-hidden className={"size-2.5 rounded-sm"} style={{backgroundColor: color}} />
    {label}
  </span>
)

// The chart's data for screen readers, one row per bucket.
const BucketsTable: React.FC<{title: string; rows: readonly TChartRow[]}> = ({title, rows}) => {
  const {t} = useTranslation()

  return (
    <table className={"sr-only"}>
      <caption>{title}</caption>
      <thead>
        <tr>
          <th scope={"col"}>{t("KlaraDashboard_UpdatesChart_Table_Bucket")}</th>
          <th scope={"col"}>{t("KlaraDashboard_UpdatesChart_Voice")}</th>
          <th scope={"col"}>{t("KlaraDashboard_UpdatesChart_Manual")}</th>
          <th scope={"col"}>{t("KlaraDashboard_UpdatesChart_Table_Total")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            <th scope={"row"}>{row.label}</th>
            <td>{row.voice}</td>
            <td>{row.manual}</td>
            <td>{row.total}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
